import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionResult, SessionPlayer } from '@spectrace/shared';

interface MiniLeaderboardRow {
  id: string;
  filterKey: string;
  driverName: string;
  teamName?: string;
  iRating?: number;
  carName?: string;
  bestLapTime?: number;
  isActive: boolean;
  lapCount: number;
  targetLaps: number;
  isFinished: boolean;
}

@Component({
  selector: 'app-mini-leaderboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mini-leaderboard.component.html',
  styleUrls: ['./mini-leaderboard.component.css'],
})
export class MiniLeaderboardComponent {
  @Input() session: SessionResult | null = null;
  @Input() selectedDriverIds: string[] = [];
  @Output() selectedDriverIdsChange = new EventEmitter<string[]>();

  get rows(): MiniLeaderboardRow[] {
    if (!this.session) {
      return [];
    }

    const players: SessionPlayer[] = this.session.sessionPlayers || [];
    const targetLaps = this.session.sessionDetails?.lapsPerDriver || 10;

    if (players.length <= 1) {
      const validLaps = (this.session.allLaps || []).filter(
        (lap) => lap.isValid !== false,
      );
      const lapCount = (this.session.allLaps || []).length;
      const bestLapTime = validLaps.length
        ? Math.min(...validLaps.map((lap) => lap.lapTime))
        : undefined;
      const hasJoinedPlayer = players.length === 1;
      const driverName = hasJoinedPlayer
        ? players[0].displayName
        : 'No players joined yet';
      const filterKey = hasJoinedPlayer
        ? players[0].id
        : `placeholder:${this.session.id}`;
      const carName = hasJoinedPlayer
        ? players[0]?.carName ||
          players[0]?.carClass ||
          this.session.sessionDetails?.carName
        : undefined;
      const teamName = this.session.isPublic ? this.session.teamName : undefined;
      const iRating = hasJoinedPlayer ? players[0]?.iRating : undefined;

      return [
        {
          id: this.session.id,
          filterKey,
          driverName,
          teamName,
          iRating,
          carName,
          bestLapTime,
          isActive: this.session.status === 'active',
          lapCount,
          targetLaps,
          isFinished: lapCount >= targetLaps,
        },
      ];
    }

    const rows: MiniLeaderboardRow[] = players.map((player) => {
      const playerLaps = (this.session?.allLaps || []).filter(
        (lap) =>
          lap.playerId === player.id ||
          (!lap.playerId && lap.playerName === player.displayName),
      );

      const validLaps = playerLaps.filter((lap) => lap.isValid !== false);
      const bestLapTime = validLaps.length
        ? Math.min(...validLaps.map((lap) => lap.lapTime))
        : undefined;

      const activeStint = (this.session?.activeStints || []).find(
        (stint) => stint.playerId === player.id && stint.status === 'active',
      );

      return {
        id: player.id,
        filterKey: player.id,
        driverName: player.displayName,
        teamName: this.session?.isPublic ? this.session.teamName : undefined,
        iRating: player.iRating,
        carName: player.carName || player.carClass,
        bestLapTime,
        isActive: !!activeStint,
        lapCount: playerLaps.length,
        targetLaps,
        isFinished: playerLaps.length >= targetLaps,
      };
    });

    return rows.sort((a, b) => {
      if (a.bestLapTime === undefined) return 1;
      if (b.bestLapTime === undefined) return -1;
      return a.bestLapTime - b.bestLapTime;
    });
  }

  toggleDriver(row: MiniLeaderboardRow): void {
    const currentlySelected = new Set(this.selectedDriverIds);

    if (currentlySelected.has(row.filterKey)) {
      currentlySelected.delete(row.filterKey);
    } else {
      currentlySelected.add(row.filterKey);
    }

    this.selectedDriverIdsChange.emit([...currentlySelected]);
  }

  isSelected(row: MiniLeaderboardRow): boolean {
    return this.selectedDriverIds.includes(row.filterKey);
  }

  formatTime(milliseconds: number): string {
    if (milliseconds <= 0) {
      return '--:--.---';
    }

    const totalSeconds = milliseconds / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const ms = Math.floor(milliseconds % 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms
      .toString()
      .padStart(3, '0')}`;
  }
}
